import React from 'react';
import { Audio, useVideoConfig, getRemotionEnvironment } from 'remotion';
import { getAudioVolume } from '../../animations/presets';
import type { AudioLayer as AudioLayerConfig, UploadedAsset } from '../../types';

interface AudioLayerProps {
  layer: AudioLayerConfig;
  asset?: UploadedAsset;
}

/**
 * Audio-only layer — no visual output.
 * Handles fade-in and fade-out via Remotion's Audio volume prop.
 *
 * NOTE on playbackRate:
 * Remotion's browser preview controls audio by seeking frame-by-frame to
 * `currentTime = frame / fps`. When playbackRate > 1 the native audio element
 * runs ahead between seeks, so Remotion constantly seeks it BACKWARD — causing
 * stutter and word-cutting in the preview. This is a known Remotion limitation.
 *
 * Fix: in preview (player) mode we keep playbackRate = 1 so the audio plays
 * cleanly. The selected speed is applied only during the server render, where
 * Remotion handles audio correctly per-frame at the chosen rate.
 */
export function AudioLayer({ layer, asset }: AudioLayerProps) {
  const { durationInFrames } = useVideoConfig();

  // Accept both 'audio' and 'video' typed assets — an audio-only .mp4 file
  // can be stored with type 'video' if the browser reported video/mp4.
  if (!asset || (asset.type !== 'audio' && asset.type !== 'video')) return null;

  // Muted — completely silence; render nothing so Remotion skips the decode.
  if (layer.muted) return null;

  // Apply playbackRate only during server render.
  // In browser preview Remotion's frame-seeking conflicts with native audio
  // playbackRate, causing backward seeks and word-cutting artifacts.
  const { isRendering } = getRemotionEnvironment();
  const playbackRate = isRendering ? (layer.playbackRate ?? 1) : 1;

  return (
    <Audio
      src={asset.url}
      volume={(frame) =>
        getAudioVolume(
          frame,
          layer.fadeIn,
          layer.fadeOut,
          durationInFrames,
          layer.volume
        )
      }
      // Loop background music; don't loop voice-over
      loop={layer.assetKey === 'backgroundMusic'}
      playbackRate={playbackRate}
    />
  );
}
